import { createUUID } from "./uuid";

export interface FileInfo {
  filePath: string;
  bucketName?: string;
  base64?: string;
}

export async function compressImage(file: File): Promise<FileInfo> {
  const resizedImage = await resizeAndCompressImageToWebP(file);
  const fileExtension = file.name.split(".").pop();
  const fileName = `${await createUUID()}.${fileExtension}`;
  const url = await getBase64FromFile(resizedImage);
  return { filePath: fileName, base64: url };
}

export function getBase64FromFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve((reader.result as string).split(";base64,").pop()!);
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function resizeAndCompressImageToWebP(
  imageFile: File,
  maxSize = 1280,
  quality = 0.8,
) {
  return new Promise<File>((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(imageFile);

    img.onload = () => {
      let width = img.width;
      let height = img.height;
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
      } else {
        reject(new Error("Could not create canvas context"));
      }
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const [fileName] = imageFile.name.split(".");
            resolve(blobToFile(blob, `${fileName}.webp`));
          } else {
            reject(new Error("Compression failed"));
          }
        },
        "image/webp",
        quality,
      );
    };

    img.onerror = reject;
  });
}

function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
}
